/**
 * The queue's assignment cell — group AND agent in one control.
 *
 * Kai: *"agent and group should be editable together in one column"*, then
 * *"the UI is still not great, can you check the capture and replicate the
 * UI"*. Built against `Reassign.html`, measured rather than eyeballed:
 *
 * | reference                                   | here                        |
 * | ------------------------------------------- | --------------------------- |
 * | one trigger, `Group / Agent`, 32px, chevron  | same                        |
 * | popover 250×450, portalled                   | 256 × 420                   |
 * | search row at the top, spanning both lists    | same                        |
 * | sticky heading: `None` + the current group    | same                        |
 * | tabs `Groups` / `Agents`, active in accent    | same                        |
 * | 37px rows, 14px text                          | same                        |
 *
 * The first attempt was two stacked pickers, one per field. That was the
 * wrong shape twice over: it doubled the row height for a single decision,
 * and it made "which agents exist" look like a property of the row rather
 * than of the group you had just chosen. The reference puts them behind one
 * control **because they are one act** — you pick a team, then a person in
 * it — and the tabs are what make that sequence visible.
 */
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search } from 'lucide-react';
import { NO_AUTOFILL } from '../../components/ui/input';
import { useAnchoredPopover } from '../../lib/useAnchoredPopover';
import { cn } from '../../lib/utils';

export interface AssignOption {
  value: string;
  label: string;
}

const POP_W = 256;
const POP_MAX_H = 420;

export function AssignPicker({
  groupId,
  groupName,
  assigneeId,
  assigneeName,
  groups,
  agentsFor,
  onCommit,
  readOnly,
}: {
  groupId: string | null | undefined;
  groupName: string | null | undefined;
  assigneeId: string | null | undefined;
  assigneeName: string | null | undefined;
  groups: AssignOption[];
  agentsFor: (groupId: string | null | undefined) => AssignOption[];
  onCommit: (patch: Record<string, unknown>) => Promise<void>;
  readOnly?: boolean;
}) {
  const { open, setOpen, pos, triggerRef, popRef } = useAnchoredPopover(POP_W, POP_MAX_H);
  const [tab, setTab] = useState<'groups' | 'agents'>('groups');
  // URL state on purpose: the picker's own typeahead buffer. It belongs to the
  // control, not the page — a half-typed surname has no business in a shared
  // queue link — and it is cleared every time the popover opens.
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const agents = useMemo(() => agentsFor(groupId), [agentsFor, groupId]);
  const q = query.trim().toLowerCase();
  const list = (tab === 'groups' ? groups : agents).filter((o) => !q || o.label.toLowerCase().includes(q));

  const commit = (patch: Record<string, unknown>) => {
    setSaving(true);
    void onCommit(patch).finally(() => setSaving(false));
  };

  const openPicker = () => {
    setQuery('');
    // Opens on the tab you are most likely to want: with no group there is
    // nothing on Agents to pick, so starting there would be a dead end.
    setTab(groupId ? 'agents' : 'groups');
    setOpen(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      disabled={readOnly}
      onClick={(e) => {
        e.stopPropagation();
        if (open) setOpen(false);
        else openPicker();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      className={cn(
        'flex w-full min-w-0 items-center gap-1 rounded border border-transparent px-2 py-1 text-left',
        // The affordance keys off `group/row`, not this cell: hovering the ROW
        // frames every editable cell at once, so one pass tells you the set.
        // See InlineCell for the full reasoning and the Tailwind ordering trap.
        !readOnly && 'group-hover/row:border-input',
        !readOnly && 'group-data-[focused]/row:border-input',
        !readOnly && 'hover:ring-1 hover:ring-ring',
        !readOnly && '[@media(hover:none)]:border-input',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        open && 'border-input ring-1 ring-ring',
        saving && 'opacity-50',
      )}
      title={readOnly ? undefined : 'Click to reassign'}
    >
      {/* Two spans, each truncating on its own — exactly as the reference
          does it. One span would let a long group name eat the agent
          entirely, which is the half you most need when triaging. */}
      <span className="min-w-0 flex-1 truncate">
        <span className={cn(!groupId && 'text-muted-foreground')}>{groupName ?? 'No group'}</span>
        <span className="text-muted-foreground"> / {assigneeName ?? 'Unassigned'}</span>
      </span>
      {!readOnly && (
        <ChevronDown
          aria-hidden
          className={cn(
            'h-3 w-3 shrink-0 text-muted-foreground transition-opacity',
            open ? 'opacity-100' : 'opacity-0',
            'group-hover/row:opacity-70 group-data-[focused]/row:opacity-70 [@media(hover:none)]:opacity-70',
          )}
        />
      )}
    </button>
  );

  return (
    <>
      {trigger}
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            role="listbox"
            style={{
              top: pos.top,
              left: pos.left,
              width: POP_W,
              ...(pos.flip ? { transform: 'translateY(-100%)' } : {}),
            }}
            className="fixed z-50 overflow-hidden rounded-md border bg-popover shadow-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search spans BOTH lists in the reference — the placeholder says
                so, because a box that only searches the tab you are on is the
                kind of thing people report as "search is broken". */}
            <div className="flex items-center gap-1.5 border-b px-2">
              <Search aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <input
                {...NO_AUTOFILL}
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Escape') setOpen(false);
                }}
                placeholder="Search by group or agent"
                className="w-full bg-transparent py-2 text-xs focus:outline-none"
              />
            </div>

            {/* The sticky heading: where you are, and the way out of it. */}
            <div className="space-y-0.5 border-b bg-muted/40 px-2 py-1.5">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  // ONE patch, not two. Clearing the group already strands the
                  // agent, and two round trips would leave a visible moment
                  // where the row is half-cleared.
                  if (groupId || assigneeId) commit({ groupId: null, assigneeId: null });
                }}
                className="w-full rounded px-1 py-0.5 text-left text-xs font-semibold hover:bg-accent"
              >
                None
              </button>
              {groupId && <p className="truncate px-1 text-xs font-semibold text-brand">{groupName}</p>}
            </div>

            <div className="flex border-b">
              {/* Real labels, not `capitalize` on the key: the accessible name
                  is the text content, and a screen reader should not be told
                  the tab is called "groups" in lower case. */}
              {(
                [
                  ['groups', 'Groups'],
                  ['agents', 'Agents'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  className={cn(
                    'flex-1 border-b-2 px-2 py-1.5 text-xs font-medium transition-colors',
                    tab === key
                      ? 'border-brand text-brand'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="max-h-60 overflow-y-auto py-1">
              {tab === 'agents' && !groupId && (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                  Pick a group first — agents come from the group.
                </p>
              )}
              {tab === 'agents' && groupId && agents.length === 0 && (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                  Nobody is in {groupName} yet.
                </p>
              )}
              {list.length === 0 && q && (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">No matches.</p>
              )}
              {list.map((o) => {
                const current = (tab === 'groups' ? groupId : assigneeId) === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={current}
                    onClick={() => {
                      if (tab === 'groups') {
                        // Stay open and step to Agents: picking a team is the
                        // FIRST half of reassigning, and closing here would
                        // make you reopen the same control to finish.
                        setQuery('');
                        setTab('agents');
                        if (o.value !== groupId) commit({ groupId: o.value });
                      } else {
                        setOpen(false);
                        if (o.value !== assigneeId) commit({ assigneeId: o.value });
                      }
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-accent',
                      current && 'font-medium',
                    )}
                  >
                    <Check className={cn('h-3 w-3 shrink-0', current ? 'opacity-100' : 'opacity-0')} />
                    <span className="min-w-0 truncate">{o.label}</span>
                  </button>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
